import { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import CustomAlert from "../components/CustomAlert";

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);
  const navigate = useNavigate();

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId(alertId + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      await updateProfile(userCredential.user, { displayName: username });
      await setDoc(doc(db, `users/${userCredential.user.uid}`), {
        createdAt: serverTimestamp(),
        subscriptionStatus: "free",
        agentsCreated: 0,
      });
      navigate("/runtime"); // redirect to homepage or dashboard
    } catch (error: any) {
      if (error.code === "auth/email-already-in-use") {
        showAlert(
          "This email is already in use. Please log in or use a different email."
        );
      } else {
        showAlert("Signup failed: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-charcoal px-4">
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}
      {loading ? (
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-10 w-10 text-teal mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-offwhite text-lg">Creating your account...</p>
        </div>
      ) : (
        <form
          onSubmit={handleSignUp}
          className="bg-secondary border border-darkgray rounded-xl p-8 w-full max-w-sm shadow-sm"
        >
          {/* <button
            type="button"
            onClick={() => navigate(-1)}
            className="block mb-4 text-teal hover:underline"
          >
            ← Back to Runtime
          </button> */}
          <h2 className="text-2xl font-semibold mb-6 text-offwhite">Sign Up</h2>
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full p-3 mb-4 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 mb-4 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-3 mb-4 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
            required
          />
          <button
            type="submit"
            className="w-full bg-teal text-offwhite py-2 rounded-lg hover:bg-teal/80 transition"
          >
            Create Account
          </button>
          <p className="text-center mt-5 font-semibold text-offwhite">
            Have an account?{" "}
            <Link
              to="/login"
              className=" text-teal hover:text-teal/80 transition"
            >
              Log In
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}
